import {
  FinancialProfile,
  Organization,
  Sector,
  User,
} from "@/modules/Auth/types";
import { NOTIFICATION_MESSAGES } from "../constants/notificationMessages";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { NOTIFICATION_TYPES } from "../enums/NOTIFICATION_TYPES";
import {
  NOTIFICATION_EVENTS,
  notificationBus,
} from "../eventBusses/notification";
import { INotification } from "../interfaces/notification.interface";
import storageService from "../services/storage.service";
import { getThemeColor, setThemeColor } from "./colortheme";
import store from "@/store";
import { AUTH_MUTATIONS } from "@/modules/Auth/vuex/mutations";
import { PERMISSIONS } from "../enums/PERMISSIONS";
import { AUTH_GETTERS } from "@/modules/Auth/vuex/getters";
import { AGENT_SUB_ROLES } from "../enums/AGENT_SUB_ROLES";
import { USER_ROLES } from "../enums/USER_ROLES";
import {
  BaggageInfo,
  DealDiscount,
  DiscountType,
  FareOption,
} from "@/core/types/flight";
import { getGrossFare, getTotalAmountAfterPricing } from "@/core/utils/flight";
import {
  AmountType,
  FareType,
  TemperFare,
} from "@/modules/FlightSearchResult/types";
import { PATH } from "../constants/path";
import { genders, passengerTypes, titles } from "../constants/bookingForm";

const UTILS = {
  getThemeColor,
  setThemeColor,

  notify(description: string | null, type = NOTIFICATION_TYPES.INFO): void {
    const data: INotification = {
      type: type,
      description: description || NOTIFICATION_MESSAGES.DEFAULT,
    };

    notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, data);
  },

  updateUser(user: User) {
    storageService.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    const organization = user.organization_user_data.organization;

    setThemeColor(organization);

    this.updateOrganization(organization);

    store.commit(AUTH_MUTATIONS.UPDATE_USER, user);
  },

  updateAuthTokens(access: string, refresh: string) {
    storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, access, false);
    storageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh, false);

    store.commit(AUTH_MUTATIONS.UPDATE_AUTH_TOKENS, { access, refresh });
  },

  updatePermissions(permissions: PERMISSIONS[]) {
    storageService.setItem(
      STORAGE_KEYS.PERMISSIONS,
      JSON.stringify(permissions),
    );

    store.commit(AUTH_MUTATIONS.PERMISSIONS, permissions);
  },

  updateOrganization(organization?: Organization) {
    if (!organization) {
      storageService.removeItem(STORAGE_KEYS.ORGANIZATION);
      storageService.removeItem(STORAGE_KEYS.FINANCIAL_PROFILES);

      store.commit(AUTH_MUTATIONS.UPDATE_ORGANIZATION, undefined);
      return;
    }

    storageService.setItem(
      STORAGE_KEYS.ORGANIZATION,
      JSON.stringify(organization),
    );

    storageService.setItem(
      STORAGE_KEYS.FINANCIAL_PROFILES,
      JSON.stringify(organization.financial_profiles),
    );

    const storedUser: User | null = storageService.getItem(STORAGE_KEYS.USER);

    if (storedUser) {
      const userWithUpdatedOrg: User = {
        ...storedUser,
        organization_user_data: {
          ...(storedUser.organization_user_data ?? {}),
          organization,
        },
      };

      storageService.setItem(
        STORAGE_KEYS.USER,
        JSON.stringify(userWithUpdatedOrg),
      );
    }

    store.commit(AUTH_MUTATIONS.UPDATE_ORGANIZATION, organization);
  },

  updateFinancialProfiles(financialProfiles: FinancialProfile[]) {
    storageService.setItem(
      STORAGE_KEYS.FINANCIAL_PROFILES,
      JSON.stringify(financialProfiles),
    );

    store.commit(AUTH_MUTATIONS.UPDATE_FINANCIAL_PROFILES, financialProfiles);
  },

  updateSectors(sectors: Sector[]) {
    storageService.setItem(STORAGE_KEYS.SECTORS, JSON.stringify(sectors));

    store.commit(AUTH_MUTATIONS.UPDATE_SECTORS, sectors);
  },

  getCurrency() {
    const currency = store.getters[AUTH_GETTERS.CURRENCY] as string | null;

    return currency;
  },

  isUser(...roles: USER_ROLES[]) {
    const user = store.getters[AUTH_GETTERS.USER_ROLE] as USER_ROLES | null;

    return user ? roles.includes(user) : false;
  },

  isAgentSubType(...subTypes: AGENT_SUB_ROLES[]) {
    const subType = store.getters[
      AUTH_GETTERS.USER_ROLE_SUB_TYPE
    ] as AGENT_SUB_ROLES | null;

    return subType ? subTypes.includes(subType) : false;
  },

  mapSectorInFinancialProfiles(
    sector: string,
    financialProfiles: FinancialProfile[],
  ): FinancialProfile[] {
    let tempFp = financialProfiles;

    tempFp = tempFp.map((fp) => {
      return { ...fp, sector };
    });

    return tempFp;
  },

  parseDiscountLabel(data: DealDiscount | null): string {
    if (!data) {
      return "";
    }

    const {
      discount_type,
      discount_on_gross_fare,
      discount_amount,
      discount_currency,
    } = data;

    const amount =
      discount_type === DiscountType.PERCENTAGE
        ? `${discount_amount}%`
        : `${discount_amount}${discount_currency}`;

    const fareType = discount_on_gross_fare ? "Gross Fare" : "Base Fare";
    return `Applied on ${fareType} with ${amount} discount`;
  },

  getBaggageInfo(baggage_info?: BaggageInfo | null): string {
    if (!baggage_info) {
      return "No Baggage";
    }

    const { pieces = 0, weight = 0, unit = "" } = baggage_info;

    if (pieces > 0) {
      if (weight > 0) {
        return `Total ${weight.toFixed(1)} ${unit} (${pieces} piece(s))`;
      } else {
        return `As per airline policy (${pieces} piece(s))`;
      }
    } else if (weight > 0) {
      return `${weight.toFixed(1)} ${unit} (As per airline policy)`;
    } else {
      return "No Baggage";
    }
  },

  getFormattedCurrency(amount: number) {
    const currency = this.getCurrency();

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency ?? "PKR",
      minimumFractionDigits: 0,
    }).format(Number(amount));
  },

  calculateFlightPrice(fareOption: FareOption, temperFare: TemperFare): number {
    const fareCalculationAmount = temperFare?.amount ?? 0;
    const fareType = temperFare?.fareType;
    const amountType = temperFare?.amountType;

    if (!fareCalculationAmount) {
      return getTotalAmountAfterPricing(fareOption);
    }
    const totalAmountAfterPricing = getTotalAmountAfterPricing(fareOption);
    let fare = getGrossFare(fareOption);
    let amountToAdd = fareCalculationAmount;

    if (fareType === FareType.FIXED) {
      fare = Number(fareOption.price.base_fare.value);
    }

    if (amountType === AmountType.PERCENTAGE) {
      amountToAdd = (fare / 100) * fareCalculationAmount;
    }

    return totalAmountAfterPricing + amountToAdd;
  },

  flightSearchResultsPath(): string {
    return PATH.FLIGHTS_SEARCH_RESULT;
  },

  toTitleCase(input: string): string {
    const convertedInput = input
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return convertedInput;
  },

  getDisplayName(): string {
    const DISPLAY_NAME = import.meta.env
      .VITE_APP_DEFAULT_DISPLAY_NAME as string;

    return DISPLAY_NAME;
  },
  getDefaultLogo(): string {
    const DEFAULT_LOGO = import.meta.env.VITE_APP_DEFAULT_LOGO as string;

    return DEFAULT_LOGO;
  },
  getDefaultColor(): string {
    const DEFAULT_COLOR = import.meta.env.VITE_APP_DEFAULT_THEME_COLOR;

    return `#${DEFAULT_COLOR}`;
  },
  getGenderLabel(value: string): string {
    const gender = genders.find((g) => g.value === value);
    return gender ? gender.label : value;
  },
  getTitleLabel(value: string): string {
    const title = titles.find((t) => t.value === value);
    return title ? title.label : value;
  },
  getPassengerTypeLabel(value: string): string {
    const paxType = passengerTypes.find((paxType) => paxType.value === value);
    return paxType ? paxType.label : value;
  },
};

export default UTILS;
